/**
 * Risk Assessment Quiz Mock Data Generator
 *
 * 风险测评问卷Mock数据生成器
 * 根据目标风险类型自动选择合适的答案
 *
 * @module mockDataGenerator.risk-assessment
 */

import { IRARiskType } from "@/types/enum";
export interface QuizDataContent {
  category: string;
  "no.": number;
  questions: QuizQuestion[];
}

export interface QuizQuestion {
  "no.": number;
  options: QuizQuestionOption[];
  question: string;
}

export interface QuizQuestionOption {
  answer: number;
  "no.": number;
  option: string;
}

/**
 * Mock配置选项
 */
export interface RiskAssessmentMockConfig {
  /**
   * 目标风险类型
   */
  targetRiskType: IRARiskType;
}

/**
 * 风险类型权重映射
 * 用于确定选择答案时的倾向性
 * 权重越高，倾向于选择选项编号越大的答案（风险越高）
 */
const RISK_TYPE_WEIGHT: Record<IRARiskType, number> = {
  [IRARiskType.IRA_RISK_CONSERVATIVE]: 0, // 保守型：选择最小编号（最低风险）
  [IRARiskType.IRA_RISK_CAUTIOUS]: 0.25, // 谨慎型：选择较小编号
  [IRARiskType.IRA_RISK_MODERATE]: 0.5, // 稳健型：选择中间编号
  [IRARiskType.IRA_RISK_AGGRESSIVE]: 0.75, // 积极型：选择较大编号
  [IRARiskType.IRA_RISK_VERY_AGGRESSIVE]: 1, // 激进型：选择最大编号（最高风险）
};

/**
 * 预设场景配置
 * 对应 strapi-enum.ts 中的 IRARiskType
 */
export const RISK_ASSESSMENT_PRESETS = {
  /** 保守型 */
  CONSERVATIVE: {
    riskType: IRARiskType.IRA_RISK_CONSERVATIVE,
    name: "保守型",
    description: "风险承受能力最低，追求资金安全",
  },
  /** 谨慎型 */
  CAUTIOUS: {
    riskType: IRARiskType.IRA_RISK_CAUTIOUS,
    name: "谨慎型",
    description: "风险承受能力较低，追求稳定收益",
  },
  /** 稳健型 */
  MODERATE: {
    riskType: IRARiskType.IRA_RISK_MODERATE,
    name: "稳健型",
    description: "风险承受能力中等，追求收益与风险平衡",
  },
  /** 积极型 */
  AGGRESSIVE: {
    riskType: IRARiskType.IRA_RISK_AGGRESSIVE,
    name: "积极型",
    description: "风险承受能力较高，追求较高收益",
  },
  /** 激进型 */
  VERY_AGGRESSIVE: {
    riskType: IRARiskType.IRA_RISK_VERY_AGGRESSIVE,
    name: "激进型",
    description: "风险承受能力最高，追求高收益",
  },
} as const;

/**
 * 生成风险测评Mock数据
 *
 * @param quizData - 题目数据
 * @param config - Mock配置
 * @returns 表单数据对象
 */
export function generateRiskAssessmentMockData(
  quizData: QuizDataContent,
  config: RiskAssessmentMockConfig
): Record<string, any> {
  const { targetRiskType } = config;
  const questions = quizData.questions;

  if (!questions || questions.length === 0) {
    return {};
  }

  const weight = RISK_TYPE_WEIGHT[targetRiskType];
  const formData: Record<string, any> = {};

  // 为每道题根据权重选择答案
  questions.forEach((question: QuizQuestion) => {
    const fieldName = `question_${question["no."]}`;
    const selectedOption = selectOptionByWeight(question, weight);
    formData[fieldName] = selectedOption["no."];
  });

  // 添加协议确认
  // formData.agreement = 1;

  return formData;
}

/**
 * 根据权重为问题选择答案选项
 *
 * @param question - 问题对象
 * @param weight - 权重 (0-1)，0表示最保守，1表示最激进
 * @returns 选中的选项
 */
function selectOptionByWeight(question: QuizQuestion, weight: number) {
  const { options } = question;

  if (!options || options.length === 0) {
    throw new Error(`Question ${question["no."]} has no options`);
  }

  // 按选项编号排序（编号越大，风险越高）
  const sortedOptions = [...options].sort((a, b) => a["no."] - b["no."]);

  // 根据权重选择对应位置的选项
  // weight = 0: 选择第一个（最小编号）
  // weight = 1: 选择最后一个（最大编号）
  // weight = 0.5: 选择中间的
  const index = Math.floor(weight * (sortedOptions.length - 1));

  return sortedOptions[index];
}

/**
 * 生成随机风险类型
 *
 * @returns 随机的风险类型
 */
export function generateRandomRiskType(): IRARiskType {
  const riskTypes = [
    IRARiskType.IRA_RISK_CONSERVATIVE,
    IRARiskType.IRA_RISK_CAUTIOUS,
    IRARiskType.IRA_RISK_MODERATE,
    IRARiskType.IRA_RISK_AGGRESSIVE,
    IRARiskType.IRA_RISK_VERY_AGGRESSIVE,
  ];

  const randomIndex = Math.floor(Math.random() * riskTypes.length);
  return riskTypes[randomIndex];
}
